// src/pages/Menu/Vinculation/MembershipApplicationsSection.jsx
import React, {
  useCallback, useEffect, useMemo, useState, useRef, useLayoutEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../../context/authContext';
import {
  FileText, CheckCircle2, XCircle, Clock, RefreshCcw, Search,
  Loader2, MoreHorizontal, ChevronDown, Eye, Mail
} from 'lucide-react';
import { Button } from '@mui/material';
import Swal from 'sweetalert2';
import {
  listMembershipApplications,
  setMembershipApplicationStatus,
} from '../../../../services/membershipApplications';
import { ActionLoader } from '../../../../components/loader';
import { Banner } from '../../../../components/common/Banner';
import SectionTips from '../../../../components/common/SectionTips';
import ArrowBack from '../../../../components/common/ArrowBack';
import TooltipHelper from '../../../../components/common/TooltipHelper';
import DetailsModal from './MembershipDetailsModal';

// Ventanas superpuestas de envío
import AcceptMailModal from './AcceptMailModal';
import RejectMailModal from './RejectMailModal';
import SendDeclarationModal from './SendDeclarationModal';

// NUEVO: ventana superpuesta de plantillas + envío (archivo separado)
import MailTemplatesModal from './MailTemplatesModal';

// Contexto de drafts compartidos
import { MailDraftsProvider } from './MailDraftsContext';
import { buildAcceptDraft, buildRejectDraft, buildAcceptanceDraft, buildReceivedDraft } from '../../../../services/mailTemplatesService';

/* ====== Filtros ====== */
const STATUS_OPTIONS = [
  { value: '',         label: 'Todos' },
  { value: 'RECEIVED', label: 'Pendientes' },
  { value: 'WAITING_RESPONSE', label: 'Esperando respuesta' },
  { value: 'ACCEPTED', label: 'Aceptadas' },
  { value: 'REJECTED', label: 'Rechazadas' },
];

/* ====== Barra de estado ====== */
function DecisionBar({ status }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const badgeRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (showTooltip && badgeRef.current) {
      const updatePosition = () => {
        const rect = badgeRef.current.getBoundingClientRect();
        setTooltipPosition({
          top: rect.bottom + 8,
          left: rect.left + rect.width / 2,
        });
      };
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showTooltip]);

  const base = 'inline-flex items-center gap-1.5 rounded-xl border px-2 lg:px-2.5 h-7 lg:h-8 min-w-0';
  
  if (status === 'ACCEPTED') {
    return (
      <div className={`${base} border-green-200 bg-green-50 max-w-full`}>
        <span className="inline-flex w-2 h-2 rounded-full bg-green-600 shrink-0" />
        <span className="text-green-800 font-semibold text-[10px] lg:text-[11px] uppercase tracking-wide truncate">Aceptada</span>
      </div>
    );
  }
  if (status === 'REJECTED') {
    return (
      <div className={`${base} border-rose-200 bg-rose-50 max-w-full`}>
        <span className="inline-flex w-2 h-2 rounded-full bg-rose-600 shrink-0" />
        <span className="text-rose-800 font-semibold text-[10px] lg:text-[11px] uppercase tracking-wide truncate">Rechazada</span>
      </div>
    );
  }
  if (status === 'WAITING_RESPONSE') {
    const tooltipContent = showTooltip && (
      <div
        ref={tooltipRef}
        className="fixed z-[99999] inline-flex items-center gap-1.5 rounded-xl border border-blue-300 bg-blue-100 px-2 lg:px-2.5 h-7 lg:h-8 shadow-lg pointer-events-none whitespace-nowrap"
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transform: 'translateX(-50%)',
        }}
      >
        <span className="inline-flex w-2 h-2 rounded-full bg-blue-600 animate-pulse shrink-0" />
        <span className="text-blue-900 font-semibold text-[10px] lg:text-[11px] uppercase tracking-wide">Esperando respuesta</span>
        {/* Flecha del tooltip */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-0.5">
          <div className="w-2 h-2 bg-blue-100 border-l border-t border-blue-300 rotate-45"></div>
        </div>
      </div>
    );

    return (
      <>
        <div 
          ref={badgeRef}
          className={`${base} border-blue-300 bg-blue-100 max-w-full min-w-fit relative`}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <span className="inline-flex w-2 h-2 rounded-full bg-blue-600 animate-pulse shrink-0" />
          <span className="text-blue-900 font-semibold text-[10px] lg:text-[11px] uppercase tracking-wide whitespace-nowrap">Esperando resp.</span>
        </div>
        {typeof document !== 'undefined' && createPortal(tooltipContent, document.body)}
      </>
    );
  }
  return (
    <div className={`${base} border-amber-200 bg-amber-50 max-w-full`}>
      <span className="inline-flex w-2 h-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
      <span className="text-amber-800 font-semibold text-[10px] lg:text-[11px] uppercase tracking-wide truncate">Pendiente</span>
    </div>
  );
}

/* ====== Helpers de fecha ====== */
function getSubmittedAt(app = {}) {
  return app.submittedAt || app.fechaPostulacion || app.createdAt || app.created_at || app.submissionDate || null;
}
function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d)) return '—';
  return new Intl.DateTimeFormat('es-CL', { dateStyle: 'medium' }).format(d);
}

/* ====== Helper para obtener nombre de empresa ====== */
function getCompanyName(app = {}) {
  return app.razonSocial || app.nombreFantasia || 'Sin nombre';
}

/* ====== Menú contextual por fila ====== */
function RowActions({ app, isAdmin, onChanged, onBusy, onOpenMail }) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const positionMenu = useCallback(() => {
    const btn = btnRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;

    const rect = btn.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const menuW = 224; // w-56
    const menuH = menu.offsetHeight || 160;
    const gap = 8;

    const openDownTop = rect.bottom + gap;
    const openUpTop = rect.top - menuH - gap;
    const openUp = openDownTop + menuH > viewportH && openUpTop >= 0;

    let left = rect.right - menuW;
    left = Math.min(left, viewportW - menuW - gap);
    left = Math.max(left, gap);

    setPos({ top: openUp ? openUpTop : openDownTop, left });
  }, []);

  useLayoutEffect(() => { if (open) positionMenu(); }, [open, positionMenu]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onClickOutside = (e) => {
      const btn = btnRef.current;
      const menu = menuRef.current;
      if (!btn || !menu) return setOpen(false);
      if (btn.contains(e.target) || menu.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [open]);

  const decide = async (status, title, text) => {
    setOpen(false);
    const confirm = await Swal.fire({
      title, text, icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#C42251',
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.isConfirmed) return;
    
    // Para ACCEPTED desde WAITING_RESPONSE, primero abrir el modal y cambiar el estado solo después de enviar el correo
    if (status === 'ACCEPTED' && app.status === 'WAITING_RESPONSE') {
      // Guardar el estado anterior para poder revertir si se cancela
      const previousStatus = app.status;
      try {
        // Cambiar estado primero
        onBusy?.(true);
        const resp = await setMembershipApplicationStatus(app.id, status);
        const updated = resp.data || resp;
        onChanged(updated);
        
        // Abrir ventana superpuesta de aceptación
        onOpenMail?.(updated, 'ACCEPTED');
        // El estado se mantendrá solo si se envía el correo exitosamente
        // Si se cancela, se revertirá en la ventana superpuesta
      } catch (e) {
        Swal.fire('Error', e?.message || 'No se pudo actualizar el estado', 'error');
      } finally {
        onBusy?.(false);
      }
      return;
    }

    try {
      onBusy?.(true);
      
      // Mostrar aviso de "Cargando..."
      Swal.fire({
        title: 'Cargando...',
        text: 'Por favor espera mientras se actualiza el estado.',
        icon: 'info',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Para REJECTED, guardar el estado anterior para poder revertir si se cancela
      const previousStatus = status === 'REJECTED' ? app.status : null;
      
      const resp = await setMembershipApplicationStatus(app.id, status);
      const updated = resp.data || resp;
      onChanged(updated);

      // 👉 abrir la ventana superpuesta correcta
      // Solo abrir ventana superpuesta de correo para REJECTED
      if (status === 'REJECTED') {
        // Guardar el estado anterior en el objeto actualizado para pasarlo al modal
        if (previousStatus) {
          updated.previousStatus = previousStatus;
        }
        onOpenMail?.(updated, 'REJECTED');
      }

      // Mensajes específicos según el estado
      let successMessage = 'Estado actualizado correctamente.';
      if (status === 'REJECTED') {
        successMessage = 'Postulación rechazada. Se abrirá la ventana superpuesta para enviar el correo de rechazo.';
      } else if (status === 'WAITING_RESPONSE') {
        successMessage = 'Estado cambiado a "Esperando respuesta". Se está esperando la declaración firmada del socio.';
      } else if (status === 'RECEIVED') {
        successMessage = 'Estado cambiado a "Pendiente". La postulación volvió al estado inicial.';
      }

      Swal.fire('Listo', successMessage, 'success');
    } catch (e) {
      Swal.fire('Error', e?.message || 'No se pudo actualizar el estado', 'error');
    } finally {
      onBusy?.(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <>
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="h-8 inline-flex items-center gap-1.5 px-3 rounded-md border bg-white text-gray-700 hover:bg-gray-50 whitespace-nowrap"
        title="Cambiar estado"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal size={16} />
        <span className="hidden lg:inline">Cambiar estado</span>
        <ChevronDown size={14} className="opacity-70 hidden lg:inline" />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="z-[1000] fixed w-56 rounded-lg border bg-white shadow-lg overflow-hidden"
          style={{ top: pos.top, left: pos.left }}
          role="menu"
        >
          {/* Enviar declaración jurada: Solo disponible cuando está en RECEIVED */}
          {app.status === 'RECEIVED' && (
            <button
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-blue-700 hover:bg-blue-50"
              onClick={() => {
                setOpen(false);
                onOpenMail?.(app, 'DECLARATION');
              }}
              role="menuitem"
            >
              <Mail size={14} /> Enviar declaración jurada
            </button>
          )}
          {/* Aceptar: Solo disponible cuando está en WAITING_RESPONSE (ya recibió la declaración firmada) */}
          {app.status === 'WAITING_RESPONSE' && (
            <button
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
              onClick={() => {
                const companyName = getCompanyName(app);
                decide('ACCEPTED', 'Marcar como aceptado', `¿Confirmas marcar la postulación de ${companyName} como Aceptada? Esto indica que el socio ya envió la declaración firmada y está listo para ingresar.`);
              }}
              role="menuitem"
            >
              <CheckCircle2 size={14} /> Marcar como aceptado
            </button>
          )}
          {/* Rechazar: Disponible siempre excepto si ya está rechazada */}
          {app.status !== 'REJECTED' && (
            <button
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-[#C42251] bg-[#FDF2F5] hover:bg-[#FBE6EE]"
              onClick={() => {
                const companyName = getCompanyName(app);
                decide('REJECTED', 'Rechazar postulación', `¿Confirmas rechazar la postulación de ${companyName}? Se abrirá la ventana superpuesta para enviar el correo de rechazo.`);
              }}
              role="menuitem"
            >
              <XCircle size={14} /> Rechazar
            </button>
          )}
          <div className="border-t my-1" />
          {/* Volver a pendiente: Siempre disponible excepto si ya está en pendiente */}
          {app.status !== 'RECEIVED' && (
            <button
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-amber-700 hover:bg-amber-50"
              onClick={() => {
                const companyName = getCompanyName(app);
                decide('RECEIVED', 'Volver a pendiente', `¿Deseas volver a dejar la postulación de ${companyName} como Pendiente? Esto revertirá el estado actual.`);
              }}
              role="menuitem"
            >
              <Clock size={14} /> Volver a pendiente
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

function SectionInner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [apps, setApps] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Detalle
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [initialTab, setInitialTab] = useState('DETAIL');

  // Ventanas superpuestas de correo separadas
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [declarationOpen, setDeclarationOpen] = useState(false);
  const [mailTarget, setMailTarget] = useState(null);

  // Ventana superpuesta de plantillas
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const openDetails = (app, tab = 'DETAIL') => { setSelected(app); setInitialTab(tab); setDetailsOpen(true); };
  const closeDetails = () => { setDetailsOpen(false); setSelected(null); setInitialTab('DETAIL'); };

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = {
        q: opts.q ?? q,
        status: opts.status ?? status,
        page: opts.page ?? page,
        pageSize: opts.pageSize ?? pageSize,
      };
      const res = await listMembershipApplications(params);
      setApps(res.data || []);
      // Usar los valores del servidor, asegurándose de mantener la página actual
      const serverMeta = res.meta || {};
      setMeta({
        page: serverMeta.page ?? params.page,
        pageSize: serverMeta.pageSize ?? params.pageSize,
        total: serverMeta.total ?? (res.data?.length || 0),
        totalPages: serverMeta.totalPages ?? Math.ceil((serverMeta.total ?? (res.data?.length || 0)) / (serverMeta.pageSize ?? params.pageSize))
      });
    } catch (e) {
      Swal.fire('Error', e?.message || 'No se pudo cargar la lista', 'error');
    } finally {
      setLoading(false);
    }
  }, [q, status, page, pageSize]);

  // Cargar cuando cambian page o pageSize
  useEffect(() => { 
    load({ page, pageSize }); 
  }, [page, pageSize, load]);
  
  // Resetear página a 1 cuando cambian los filtros (q o status)
  // IMPORTANTE: No incluir 'load' en las dependencias para evitar bucles
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      // Llamar directamente con los valores actuales sin depender de 'load'
      const params = {
        q,
        status,
        page: 1,
        pageSize,
      };
      listMembershipApplications(params).then((res) => {
        setApps(res.data || []);
        const serverMeta = res.meta || {};
        setMeta({
          page: serverMeta.page ?? 1,
          pageSize: serverMeta.pageSize ?? pageSize,
          total: serverMeta.total ?? (res.data?.length || 0),
          totalPages: serverMeta.totalPages ?? Math.ceil((serverMeta.total ?? (res.data?.length || 0)) / (serverMeta.pageSize ?? pageSize))
        });
        setLoading(false);
      }).catch((e) => {
        Swal.fire('Error', e?.message || 'No se pudo cargar la lista', 'error');
        setLoading(false);
      });
    }, 400);
    return () => clearTimeout(t);
  }, [q, status, pageSize]);

  const summary = useMemo(() => {
    const count = apps.length;
    const byStatus = {
      RECEIVED: apps.filter(a => a.status === 'RECEIVED').length,
      WAITING_RESPONSE: apps.filter(a => a.status === 'WAITING_RESPONSE').length,
      ACCEPTED: apps.filter(a => a.status === 'ACCEPTED').length,
      REJECTED: apps.filter(a => a.status === 'REJECTED').length,
    };
    return { count, byStatus };
  }, [apps]);

  const clearFilters = () => {
    setQ(''); setStatus(''); setPage(1);
    load({ q: '', status: '', page: 1 });
  };

  const shortText = (s, n = 28) => (s && s.length > n ? s.slice(0, n - 1) + '…' : (s || '—'));

  const handleChanged = (updated) => {
    setApps((prev) => prev.map((a) => (a.id === (updated.id || updated.data?.id) ? (updated.data || updated) : a)));
    setSelected((prev) => prev && (prev.id === (updated.id || updated.data?.id)) ? (updated.data || updated) : prev);
  };

  // Abrir modales según decisión
  const handleOpenMail = (updatedApp, tpl) => {
    // Si es para enviar declaración jurada (desde RECEIVED), usar el modal de declaración
    if (tpl === 'DECLARATION' || (tpl === 'ACCEPTED' && updatedApp?.status === 'RECEIVED')) {
      setMailTarget(updatedApp);
      setDeclarationOpen(true);
      return;
    }
    setMailTarget(updatedApp);
    if (tpl === 'ACCEPTED') setAcceptOpen(true);
    if (tpl === 'REJECTED') setRejectOpen(true);
  };

  // initial drafts (genéricos por defecto)
  const initialAccept = useMemo(() => buildAcceptDraft({}), []);
  const initialReject = useMemo(() => buildRejectDraft({}), []);
  const initialAcceptance = useMemo(() => buildAcceptanceDraft({}), []);
  const initialReceived = useMemo(() => buildReceivedDraft({}), []);

  return (
    <>
      <div className="relative">
        <div className="px-1 pb-2">
          <ArrowBack />
        </div>
        <Banner
          title="Postulaciones de Membresía"
          message="Haz clic en una fila para ver el detalle. Adjuntos, PDF y comunicaciones están dentro de la ventana superpuesta."
          className="text-center"
        />
      </div>

      <SectionTips
        section="las postulaciones de membresía"
        buttonTitle="¿Cómo usar esta sección?"
        tipsTitle="¿Cómo usar esta sección? de Postulaciones de membresía"
        tipsList={[
          'En esta sección podrás ver la lista de postulantes que han contestado el formulario de ingreso, puedes revisar sus respuestas y gestionar el estado de las postulaciones.',
          'Haz clic en <strong>"Ver"</strong> o en la fila de una postulación para abrir su información, puedes ver las respuestas y documentos que adjuntó al formulario, ver y editar el valor de la membresía, generar el documento de declaración jurada y la ficha de postulación para el directorio.',
          'Puedes gestionar el estado de las postulaciones en <strong>"Cambiar estado"</strong>, cuando el postulante responde el formulario ingresa en estado <strong>Pendiente</strong>, puedes decidir enviar un correo automático con la declaración jurada y su estado cambiará a <strong>Esperando Respuesta</strong>, luego puedes aceptar o, en cualquier momento, rechazar la postulación. En todos los cambios de estado se enviará un correo de aviso al postulante. También puedes cambiar el estado a pendiente nuevamente para editar alguna información.',
          'El apartado <strong>"Configuración de correos"</strong> te permite configurar las direcciones de correo del sistema, enviar correos personalizados a cualquier postulante desde esta plataforma, y también puedes revisar y editar las plantillas de los correos automáticos.',
        ]}
        finalNote=""
        color="#0488AF"
        videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766417935/Video_Tutorial_m%C3%B3dulo_Vinculaci%C3%B3n_Empresarial_-_Panel_de_Postulaciones_sqoqit.mp4"
        manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412955/Manual_de_usuario_m%C3%B3dulo_de_vinculaci%C3%B3n_empresarial_af2jv3.pdf"
      />

      {/* Controles */}
      <div className="rounded-2xl border border-gray-200 bg-white p-3 md:p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 items-center lg:grid-cols-[1fr_auto_auto_auto_auto]">
          <div className="relative">
            <div className="flex items-center gap-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Razón social, RUT, industria…"
                className="w-full h-11 pl-10 pr-4 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-[#0488AF] outline-none text-sm text-[#09274A] placeholder:text-gray-400"
              />
              <TooltipHelper text="Busca postulaciones por razón social, RUT de la empresa o industria. La búsqueda es en tiempo real." />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[#09274A] whitespace-nowrap tracking-wide uppercase">
              Estado
            </span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-11 px-3 rounded-2xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none text-sm text-[#09274A]"
              title="Estado"
            >
              {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <TooltipHelper text="Filtra las postulaciones por su estado: Pendientes (recién recibidas), Esperando respuesta (enviaste declaración jurada), Aceptadas o Rechazadas." />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[#09274A] tracking-wide uppercase">
              Por página
            </span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-11 px-3 rounded-2xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none text-sm text-[#09274A]"
              title="Tamaño de página"
            >
              {[10, 20, 30, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <TooltipHelper text="Selecciona cuántas postulaciones quieres ver por página. Útil cuando hay muchas postulaciones." />
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="contained"
              size="small"
              startIcon={<Mail size={18} />}
              sx={{
                background: 'linear-gradient(to right, #09274A, #0488AF)',
                color: '#fff',
                textTransform: 'none',
                minWidth: 140,
                minHeight: '40px',
                padding: '6px 16px',
                lineHeight: '1.5',
                '&:hover': {
                  background: 'linear-gradient(to right, #071f3d, #03779C)',
                },
              }}
              title="Configuración de correos, envío personalizado y plantillas"
              onClick={() => setTemplatesOpen(true)}
            >
              Configuración de correos
            </Button>
            <TooltipHelper text="Configura las direcciones de correo, envía correos personalizados a postulantes o edita las plantillas de correos automáticos (aceptación, rechazo, declaración jurada)." />
          </div>

        <div className="flex items-center justify-end">
          <Button
            variant="contained"
            size="small"
            startIcon={<RefreshCcw size={18} />}
            sx={{
              background: 'linear-gradient(to right, #09274A, #0488AF)',
              color: '#fff',
              textTransform: 'none',
              minWidth: 140,
              minHeight: '40px',
              padding: '6px 16px',
              lineHeight: '1.5',
              '&:hover': {
                background: 'linear-gradient(to right, #071f3d, #03779C)',
              },
            }}
            title="Refrescar"
            onClick={() => load()}
          >
            Refrescar
          </Button>
        </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 md:p-6 shadow-sm">
        {/* Resumen (tarjetas mejoradas) */}
        <div className="mb-6 space-y-4">
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl border border-blue-200 px-6 py-4">
            <div className="flex items-center justify-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#0488AF]/20 text-[#0488AF] flex items-center justify-center">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">
                  Resumen de postulaciones
                </h3>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {/* Total */}
            <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-blue-50 to-white px-3.5 py-4 shadow-sm hover:shadow-md transition-all">
              <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#0488AF] via-[#0B4A8F] to-[#37B7D4]" />
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-wide text-slate-600 uppercase leading-none text-center">
                    Total
                  </div>
                  <div className="text-xl font-bold text-[#0488AF] leading-none mt-0.5 text-center">
                    {summary.count}
                  </div>
                </div>
                <div className="h-8 w-8 rounded-md bg-white/80 border border-blue-100 flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4 text-[#0488AF]" />
                </div>
              </div>
            </div>

            {/* Pendientes */}
            <div className="group relative overflow-hidden rounded-xl border bg-white px-3.5 py-4 shadow-sm hover:shadow-md transition-all">
              <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-amber-400 to-amber-600" />
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-wide text-slate-600 uppercase leading-none text-center">
                    Pendientes
                  </div>
                  <div className="text-xl font-bold text-amber-700 leading-none mt-0.5 text-center">
                    {summary.byStatus.RECEIVED}
                  </div>
                </div>
                <div className="h-8 w-8 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4 text-amber-700" />
                </div>
              </div>
            </div>

            {/* Esperando respuesta */}
            <div className="group relative overflow-hidden rounded-xl border bg-white px-3.5 py-4 shadow-sm hover:shadow-md transition-all">
              <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-blue-400 to-blue-600" />
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-wide text-slate-600 uppercase leading-none text-center">
                    Esperando respuesta
                  </div>
                  <div className="text-xl font-bold text-blue-700 leading-none mt-0.5 text-center">
                    {summary.byStatus.WAITING_RESPONSE}
                  </div>
                </div>
                <div className="h-8 w-8 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-blue-700" />
                </div>
              </div>
            </div>

            {/* Aceptadas */}
            <div className="group relative overflow-hidden rounded-xl border bg-white px-3.5 py-4 shadow-sm hover:shadow-md transition-all">
              <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-emerald-400 to-emerald-600" />
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-wide text-slate-600 uppercase leading-none text-center">
                    Aceptadas
                  </div>
                  <div className="text-xl font-bold text-emerald-700 leading-none mt-0.5 text-center">
                    {summary.byStatus.ACCEPTED}
                  </div>
                </div>
                <div className="h-8 w-8 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                </div>
              </div>
            </div>

            {/* Rechazadas */}
            <div className="group relative overflow-hidden rounded-xl border bg-white px-3.5 py-4 shadow-sm hover:shadow-md transition-all">
              <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-rose-400 to-rose-600" />
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-wide text-slate-600 uppercase leading-none text-center">
                    Rechazadas
                  </div>
                  <div className="text-xl font-bold text-rose-700 leading-none mt-0.5 text-center">
                    {summary.byStatus.REJECTED}
                  </div>
                </div>
                <div className="h-8 w-8 rounded-md bg-rose-50 border border-rose-200 flex items-center justify-center shrink-0">
                  <XCircle className="w-4 h-4 text-rose-700" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla (listado mejorado) */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="animate-spin mr-2" /> Cargando…
          </div>
        ) : apps.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Clock className="mx-auto mb-2" />
            No hay postulaciones que coincidan con el filtro.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border bg-slate-50/40">
            <table className="w-full text-sm align-middle table-fixed">
              <thead className="bg-slate-200 text-slate-800 border-b-2 border-slate-300">
                <tr className="text-center text-sm font-bold uppercase tracking-wide">
                  <th className="w-[22%] px-4 md:px-5 py-4 text-center">Empresa</th>
                  <th className="w-[11%] px-4 md:px-5 py-4 text-center">RUT</th>
                  <th className="w-[14%] px-4 md:px-5 py-4 text-center">Fecha de postulación</th>
                  <th className="w-[11%] px-4 md:px-5 py-4 text-center">Comuna</th>
                  <th className="w-[13%] px-4 md:px-5 py-4 text-center">Estado</th>
                  <th className="w-[10%] px-4 md:px-5 py-4 text-center">Ver</th>
                  <th className="w-[19%] px-4 md:px-5 py-4 text-center">Cambiar estado</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {apps.map((app, idx) => (
                  <tr
                    key={app.id}
                    className={`group cursor-pointer transition-colors ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'
                    } hover:bg-blue-50/60`}
                    onClick={() => openDetails(app)}
                  >
                    {/* Empresa */}
                    <td className="px-3 md:px-4 py-3 md:py-4 text-left align-top">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-blue-50 border border-blue-100 shrink-0 group-hover:bg-blue-100">
                          <FileText className="w-4 h-4 text-[#0488AF]" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-[#0488AF] block truncate">
                            {app.razonSocial || app.nombreFantasia || 'Sin nombre'}
                          </div>
                          <div className="mt-0.5 text-[11px] text-slate-500 truncate">
                            Industria: {shortText(app.actividadRubro, 40) || 'Sin industria registrada'}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* RUT */}
                    <td className="px-3 md:px-4 py-3 md:py-4 align-middle text-center">
                      <div className="flex items-center justify-center h-full">
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                          {shortText(app.rutEmpresa, 20)}
                        </span>
                      </div>
                    </td>

                    {/* Fecha */}
                    <td className="px-3 md:px-4 py-3 md:py-4 align-middle text-center text-slate-700">
                      <div className="flex items-center justify-center h-full">
                        {formatDate(getSubmittedAt(app))}
                      </div>
                    </td>

                    {/* Comuna */}
                    <td className="px-3 md:px-4 py-3 md:py-4 align-middle text-center">
                      <div className="flex items-center justify-center h-full">
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 max-w-[140px] truncate">
                          {shortText(app.comuna, 22)}
                        </span>
                      </div>
                    </td>

                    {/* Estado */}
                    <td className="px-3 md:px-4 py-3 md:py-4 align-middle text-center">
                      <div className="flex items-center justify-center">
                        <DecisionBar status={app.status} />
                      </div>
                    </td>

                    {/* Ver */}
                    <td className="px-3 md:px-4 py-3 md:py-4 align-middle text-center">
                      <div className="flex items-center justify-center h-full">
                        <button
                          onClick={(e) => { e.stopPropagation(); openDetails(app); }}
                          className="inline-flex items-center gap-1 px-3 h-8 rounded-md border bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium"
                          title="Ver postulación"
                        >
                          <Eye size={16} className="text-[#0488AF]" /> Ver
                        </button>
                      </div>
                    </td>

                    {/* Cambiar estado */}
                    <td
                      className="px-3 md:px-4 py-3 md:py-4 align-middle text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-center h-full">
                        <RowActions
                          app={app}
                          isAdmin={isAdmin}
                          onChanged={handleChanged}
                          onBusy={setBusy}
                          onOpenMail={handleOpenMail}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginación */}
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-600">
            Página {meta.page} de {meta.totalPages} • {meta.total} resultados
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPage((p) => Math.max(1, p - 1));
              }}
              disabled={meta.page <= 1}
              className={`px-3 py-2 rounded-lg border text-sm ${
                meta.page <= 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white hover:bg-gray-50 text-gray-700'
              }`}
            >
              Anterior
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPage((p) => Math.min(meta.totalPages || 1, p + 1));
              }}
              disabled={meta.page >= (meta.totalPages || 1)}
              className={`px-3 py-2 rounded-lg border text-sm ${
                meta.page >= (meta.totalPages || 1)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white hover:bg-gray-50 text-gray-700'
              }`}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>

      {/* Ventana superpuesta Detalle */}
      <DetailsModal
        app={selected}
        open={detailsOpen}
        initialTab={initialTab}
        onClose={closeDetails}
        onChanged={handleChanged}
        onBusy={setBusy}
      />

      {/* Ventana superpuesta Enviar Declaración Jurada */}
      {declarationOpen && mailTarget && (
        <SendDeclarationModal
          open={declarationOpen}
          app={mailTarget}
          onClose={() => { setDeclarationOpen(false); setMailTarget(null); }}
          onChanged={(u) => { handleChanged(u); }}
        />
      )}

      {/* Ventana superpuesta Aceptación (solo cuando está en WAITING_RESPONSE y se cambia a ACCEPTED) */}
      {acceptOpen && mailTarget && (
        <AcceptMailModal
          open={acceptOpen}
          app={mailTarget}
          previousStatus="WAITING_RESPONSE"
          onClose={() => { setAcceptOpen(false); setMailTarget(null); }}
          onChanged={(u) => { handleChanged(u); }}
        />
      )}

      {/* Ventana superpuesta Rechazo */}
      {rejectOpen && mailTarget && (
        <RejectMailModal
          open={rejectOpen}
          app={mailTarget}
          previousStatus={mailTarget.previousStatus}
          onClose={() => { setRejectOpen(false); setMailTarget(null); }}
          onChanged={(u) => { handleChanged(u); }}
        />
      )}

      {/* Ventana superpuesta Plantillas + Envío */}
      <MailTemplatesModal open={templatesOpen} onClose={() => setTemplatesOpen(false)} />

      <ActionLoader open={busy} onClose={() => setBusy(false)} />
    </>
  );
}

/* ====== Provider global (scope fijo) ======
   Mantiene drafts globales y evita reseteos al cambiar de postulación.
*/
export default function MembershipApplicationsSection() {
  return (
    <MailDraftsProvider
      app={{ id: 'TEMPLATES_SCOPE' }}
      initialAccept={buildAcceptDraft({})}
      initialReject={buildRejectDraft({})}
      initialAcceptance={buildAcceptanceDraft({})}
      initialReceived={buildReceivedDraft({})}
    >
      <div className="max-w-screen-xl mx-auto px-4 py-8 space-y-6">
        <SectionInner />
      </div>
    </MailDraftsProvider>
  );
}
